/**
 * Easing Functions Module
 * Provides reusable easing functions for animations and gradients
 */

export const EASING_FUNCTION = {
  LINEAR: 'linear',
  EASE_IN_QUAD: 'ease-in-quad',
  EASE_OUT_QUAD: 'ease-out-quad',
  EASE_IN_OUT_QUAD: 'ease-in-out-quad',
  EASE_IN_CUBIC: 'ease-in-cubic',
  EASE_OUT_CUBIC: 'ease-out-cubic',
  EASE_IN_OUT_CUBIC: 'ease-in-out-cubic',  // Smoothstep
  EASE_IN_QUINT: 'ease-in-quint',
  EASE_OUT_QUINT: 'ease-out-quint',
  EASE_IN_OUT_QUINT: 'ease-in-out-quint',
  EASE_IN_SINE: 'ease-in-sine',
  EASE_OUT_SINE: 'ease-out-sine',
  EASE_IN_OUT_SINE: 'ease-in-out-sine',
  EASE_IN_EXPO: 'ease-in-expo',
  EASE_OUT_EXPO: 'ease-out-expo',
  EASE_IN_OUT_EXPO: 'ease-in-out-expo'
};

export const DEFAULT_EASING_FUNCTION = EASING_FUNCTION.EASE_OUT_SINE;

/**
 * Apply easing function to normalized value (0-1 range) for artistic gradient control
 * @param {number} t - Normalized input value (0 to 1)
 * @param {string} easingType - Easing function type from EASING_FUNCTION enum
 * @returns {number} - Eased output value (0 to 1)
 */
export function applyEasing(t, easingType = DEFAULT_EASING_FUNCTION) {
  // Clamp input to [0, 1] range
  t = Math.max(0, Math.min(1, t));

  switch (easingType) {
    case EASING_FUNCTION.LINEAR:
      return t;

    case EASING_FUNCTION.EASE_IN_QUAD:
      return t * t;

    case EASING_FUNCTION.EASE_OUT_QUAD:
      return t * (2 - t);

    case EASING_FUNCTION.EASE_IN_OUT_QUAD:
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    case EASING_FUNCTION.EASE_IN_CUBIC:
      return t * t * t;

    case EASING_FUNCTION.EASE_OUT_CUBIC:
      return (--t) * t * t + 1;

    case EASING_FUNCTION.EASE_IN_OUT_CUBIC:
      return t * t * (3 - 2 * t); // Smoothstep

    case EASING_FUNCTION.EASE_IN_QUINT:
      return t * t * t * t * t;

    case EASING_FUNCTION.EASE_OUT_QUINT:
      return (--t) * t * t * t * t + 1;

    case EASING_FUNCTION.EASE_IN_OUT_QUINT:
      return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * (--t) * t * t * t * t;

    case EASING_FUNCTION.EASE_IN_SINE:
      return 1 - Math.cos((t * Math.PI) / 2);

    case EASING_FUNCTION.EASE_OUT_SINE:
      return Math.sin((t * Math.PI) / 2);

    case EASING_FUNCTION.EASE_IN_OUT_SINE:
      return -(Math.cos(Math.PI * t) - 1) / 2;

    case EASING_FUNCTION.EASE_IN_EXPO:
      return t === 0 ? 0 : Math.pow(2, 10 * t - 10);

    case EASING_FUNCTION.EASE_OUT_EXPO:
      return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);

    case EASING_FUNCTION.EASE_IN_OUT_EXPO:
      return t === 0 ? 0 : t === 1 ? 1 : t < 0.5
        ? Math.pow(2, 20 * t - 10) / 2
        : (2 - Math.pow(2, -20 * t + 10)) / 2;

    default:
      // Default to smoothstep (ease-in-out-cubic)
      return t * t * (3 - 2 * t);
  }
}
